/*
 * <availability-gate> — keeps the add-to-cart UI honest across a client-side
 * variant change.
 *
 * THE DEFECT THIS EXISTS TO PREVENT
 * The old theme recomputed the buy button from `currentVariant.available` on every
 * variant change (assets/global.js:605). Shopify's own availability flag knows
 * nothing about the stock metafield gate, so the moment a shopper switched variant
 * the gate was discarded and a contract-unavailable variant became purchasable.
 *
 * THE RULE HERE
 * This element never decides anything. Availability is decided once, in Liquid, by
 * snippets/availability.liquid. Dawn's variant change re-requests the section from
 * Shopify, which re-runs that contract for the newly selected variant; this element
 * copies the freshly rendered server verdict onto the live DOM. There is no
 * client-side reimplementation of the rule to drift out of sync, and no reachable
 * path that turns the button back on from raw Shopify availability.
 *
 * FAIL CLOSED
 * If the re-rendered markup cannot be read for any reason — request shape changed,
 * ids renamed, section stripped — the element locks the button rather than leaving
 * it enabled for a variant it cannot vouch for.
 *
 * The element renders `hidden`, so it has no layout of its own and needs no CSS.
 */
if (!customElements.get('availability-gate')) {
  customElements.define(
    'availability-gate',
    class AvailabilityGate extends HTMLElement {
      connectedCallback() {
        // Reassert the server verdict on connect. Covers the whole-node swap path
        // (product-info handleSwapProduct), where this element arrives fresh from
        // the server but the button is re-parented rather than re-initialised.
        this.applyState(
          this.dataset.availabilityPurchasable === 'true' && !this.submitButton?.hasAttribute('disabled'),
          null
        );

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) =>
            this.onVariantChange(event)
          );
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      get submitButton() {
        return document.getElementById(this.dataset.submitButton);
      }

      get dynamicCheckout() {
        return this.dataset.dynamicCheckout ? document.getElementById(this.dataset.dynamicCheckout) : null;
      }

      /*
       * EVERY /cart/add form in this product's scope, not just our own.
       * Dawn renders a second one for `payment_terms` (inside the price block), which
       * carries a live variant id. The server gates both; without this the client-side
       * variant change only re-gated ours and the two disagreed until the next reload.
       * Scoped to the product-info element — or the section — so a gate on one product
       * can never reach into another.
       */
      get variantIdInputs() {
        const scope = this.closest('product-info') || this.closest('section') || this.closest('form');
        if (!scope) return [];
        return [...scope.querySelectorAll('form[action*="/cart/add"] input[name="id"]')];
      }

      /*
       * Dawn identifies a section by `originalSection || section` on <product-info>.
       * Ids inside the re-rendered document are suffixed with that same value, while
       * ids in the live DOM use the rendered section id. Matching on the ancestor
       * rather than on our own attribute is what keeps this correct inside the
       * quick-add modal, where the two differ.
       */
      belongsToSection(sectionId) {
        const productInfo = this.closest('product-info');
        if (!productInfo) return this.dataset.sectionId === sectionId;
        return (productInfo.dataset.originalSection || productInfo.dataset.section) === sectionId;
      }

      onVariantChange(event) {
        const data = event && event.data ? event.data : {};
        const sectionId = data.sectionId;
        const html = data.html;

        if (!this.belongsToSection(sectionId)) return;

        if (!html) {
          this.lock();
          return;
        }

        const sourceGate = html.getElementById(`AvailabilityGate-${sectionId}`);
        const sourceButton = html.getElementById(`ProductSubmitButton-${sectionId}`);

        if (!sourceGate || !sourceButton) {
          this.lock();
          return;
        }

        // P5: the label lives in a dedicated node so writing it cannot destroy
        // the cart icon sitting beside it inside the wrapper span. The bare
        // <span> fallback keeps older render sites working.

        this.dataset.availabilityState = sourceGate.dataset.availabilityState || '';
        this.dataset.availabilityPurchasable = sourceGate.dataset.availabilityPurchasable || 'false';
        this.dataset.availabilityReason = sourceGate.dataset.availabilityReason || '';

        // The server-rendered `disabled` already folds in every reason the button can
        // be closed: the contract, the quantity rule, and a null variant. Trust it.
        const purchasable =
          sourceGate.dataset.availabilityPurchasable === 'true' && !sourceButton.hasAttribute('disabled');

        this.applyState(
          purchasable,
          (sourceButton.querySelector('[data-buy-button-label]') || sourceButton.querySelector('span'))?.textContent
        );
      }

      lock() {
        this.dataset.availabilityPurchasable = 'false';
        this.dataset.availabilityReason = 'render-unreadable';
        this.applyState(false, null);
      }

      applyState(purchasable, label) {
        const button = this.submitButton;
        if (button) {
          // Both flags: `disabled` blocks pointer and implicit form submission,
          // `aria-disabled` is what product-form.js checks before it POSTs.
          button.toggleAttribute('disabled', !purchasable);
          button.setAttribute('aria-disabled', String(!purchasable));

          const labelNode = button.querySelector('[data-buy-button-label]') || button.querySelector('span');
          if (labelNode && typeof label === 'string' && label.trim() !== '') {
            labelNode.textContent = label.trim();
          }
        }

        const dynamicCheckout = this.dynamicCheckout;
        if (dynamicCheckout) dynamicCheckout.toggleAttribute('hidden', !purchasable);

        this.variantIdInputs.forEach((input) => {
          input.disabled = !purchasable;
        });
      }
    }
  );
}
